import { Component, OnInit, OnDestroy, ViewChild, Input } from '@angular/core';
import { BusinessUnitBricksService } from '../business-unit-bricks.service';
import { HttpEventType } from '@angular/common/http';
import { ModalDirective } from 'ngx-bootstrap';
import { Subscription, interval } from 'rxjs';
import { switchMap } from 'rxjs/operators';

@Component({
  selector: 'crm-provision-business-unit-bricks',
  templateUrl: './provision-business-unit-bricks.component.html',
  styleUrls: ['./provision-business-unit-bricks.component.css']
})
export class ProvisionBusinessUnitBricksComponent implements OnInit, OnDestroy {

  // same flow as the HCO "update from CSV" upload: send the file (upload %), then poll the background job (processing %)
  loading: boolean;
  error: boolean;
  message: string;
  details: string[] = [];
  progressPercent: number;
  progressPhase: string;
  jobId: string;
  hasSheet: boolean;
  @Input() isSalesBrick: boolean;

  @ViewChild("provisionModal")
  provisionModal: ModalDirective;
  private pollSub: Subscription;

  constructor(private businessUnitService: BusinessUnitBricksService) { }

  ngOnInit() {
    if (typeof (this.isSalesBrick) == 'string') {
      if (this.isSalesBrick == 'false')
        this.isSalesBrick = false;
      else if (this.isSalesBrick == 'true')
        this.isSalesBrick = true;
    }
  }

  ngOnDestroy() {
    if (this.pollSub) this.pollSub.unsubscribe();
  }

  reset() {
    if (this.pollSub) this.pollSub.unsubscribe();
    this.loading = false;
    this.error = false;
    this.message = null;
    this.details = [];
    this.progressPercent = 0;
    this.progressPhase = null;
    this.jobId = null;
    this.hasSheet = false;
  }

  downloadTemplate() {
    this.businessUnitService.downloadCsvTemplate().subscribe(response => {
      const blob = new Blob([response], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      var anchor = document.createElement("a");
      anchor.download = "BusinessUnitsBricks.csv";
      anchor.href = url;
      anchor.click();
    },
      err => { console.log(err); });
  }

  upload(fileInput) {
    const files = fileInput.files;
    if (files.length === 0)
      return;

    this.reset();
    const formData = new FormData();
    for (let file of files)
      formData.append(file.name, file);
    fileInput.value = "";   // so choosing the same file again triggers a new upload

    this.loading = true;
    this.progressPhase = "Uploading file";
    this.message = "Uploading .....";
    this.businessUnitService.provisionBusinessUnitsBrick(formData, this.isSalesBrick).subscribe(event => {
      if (event.type === HttpEventType.UploadProgress && event.total)
        this.progressPercent = Math.round(100 * event.loaded / event.total);
      else if (event.type === HttpEventType.Response) {
        this.jobId = (event.body as any).jobId;
        this.progressPercent = 0;
        this.progressPhase = "Processing";
        this.message = "Processing .....";
        this.pollStatus();
      }
    },
      err => this.fail(typeof err.error === "string" ? err.error : "The file could not be uploaded. Nothing was saved."));
  }

  private pollStatus() {
    this.pollSub = interval(1500).pipe(
      switchMap(() => this.businessUnitService.getProvisionStatus(this.jobId))
    ).subscribe((status: any) => {
      this.progressPhase = status.phase;
      this.progressPercent = status.totalRows > 0
        ? Math.min(100, Math.round(100 * status.processedRows / status.totalRows))
        : 0;

      if (status.state === "Failed") {
        this.pollSub.unsubscribe();
        this.fail("The upload failed: " + (status.errorMessage || "unexpected error") + ". Nothing was saved.");
      }
      else if (status.state === "Completed") {
        this.pollSub.unsubscribe();
        this.showResult(status.result);
      }
    },
      err => {
        this.pollSub.unsubscribe();
        this.fail("Lost track of the upload. Reload the bricks list to see what was saved.");
      });
  }

  private showResult(r: any) {
    this.loading = false;
    this.hasSheet = r.hasSheet;
    if (!r.saved) {
      this.error = true;
      this.message = r.errorMessage || "Nothing from this file was saved.";
    }
    else {
      this.error = r.rejected > 0;
      this.message = r.rejected > 0
        ? `Uploaded with errors: ${r.added} added, ${r.rejected} rejected (not saved).`
        : `Uploaded successfully: ${r.added} added.`;
    }
    this.details = [
      `Rows in file: ${r.totalRows}`,
      `Added: ${r.saved ? r.added : 0}`,
      `Skipped (already in the business unit, or repeated in the file): ${r.alreadyExisting}`,
      `Rejected (not saved): ${r.rejected}`
    ];
    if (r.saved && r.rejected > 0)
      this.details.push("All valid rows were saved; only the rejected rows were not.");
    // the links were saved, but re-spreading the shared bricks percentages afterwards failed
    if (r.saved && r.percentWarning) {
      this.error = true;
      this.message = `${r.added} added, but: ${r.percentWarning}`;
    }
    // like the other uploads: the error sheet (every rejected and skipped row, with its reason) downloads by itself
    if (this.hasSheet) {
      this.details.push("The error sheet with each rejected / skipped row and its reason has been downloaded.");
      this.downloadErrorSheet();
    }
  }

  private fail(message: string) {
    this.loading = false;
    this.error = true;
    this.message = message;
  }

  private downloadErrorSheet() {
    this.businessUnitService.downloadProvisionResultSheet(this.jobId).subscribe(response => {
      const blob = new Blob([response], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      var anchor = document.createElement("a");
      anchor.download = "BusinessUnitsBricksUploadErrors.csv";
      anchor.href = url;
      anchor.click();
    },
      err => { console.log(err); });
  }
}
